/**
 * Ant Design Theme Configuration
 * Uses CSS variables for better performance and runtime theming
 */

import type { ThemeConfig } from 'antd'

/**
 * Main theme configuration
 * Uses CSS variables (Ant Design 6 feature) for better performance
 */
export const theme: ThemeConfig = {
  cssVar: { prefix: 'app' }, // Enable CSS variables with prefix
  hashed: false, // Use readable CSS class names (easier debugging)

  token: {
    // Primary color
    colorPrimary: '#1890ff',

    // Border radius
    borderRadius: 6,

    // Font
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,

    // Spacing
    marginXS: 8,
    marginSM: 12,
    margin: 16,
    marginMD: 20,
    marginLG: 24,
    marginXL: 32,

    // Colors
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    colorInfo: '#1890ff',

    // Link
    colorLink: '#1890ff',
    colorLinkHover: '#40a9ff',
    colorLinkActive: '#096dd9',

    // Text
    colorTextBase: '#000000',
    colorTextSecondary: 'rgba(0, 0, 0, 0.65)',
    colorTextTertiary: 'rgba(0, 0, 0, 0.45)',
    colorTextQuaternary: 'rgba(0, 0, 0, 0.25)',

    // Background
    colorBgBase: '#ffffff',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgLayout: '#f0f2f5',

    // Border
    colorBorder: '#d9d9d9',
    colorBorderSecondary: '#f0f0f0',
  },

  components: {
    // Button
    Button: {
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
    },

    // Input
    Input: {
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
    },

    // Layout
    Layout: {
      headerBg: '#ffffff',
      siderBg: '#ffffff',
      bodyBg: '#f0f2f5',
      headerHeight: 64,
    },

    // Message
    Message: {
      contentBg: '#ffffff',
    },

    // Notification
    Notification: {
      width: 384,
    },

    // Modal
    Modal: {
      borderRadiusLG: 8,
    },

    // Card
    Card: {
      borderRadiusLG: 8,
      colorBgContainer: '#ffffff',
    },

    // Table
    Table: {
      headerBg: '#fafafa',
      headerSplitColor: '#f0f0f0',
    },
  },
}

/**
 * Dark theme configuration
 */
export const darkTheme: ThemeConfig = {
  ...theme,
  token: {
    ...theme.token,
    // Background colors
    colorBgBase: '#141414',
    colorBgContainer: '#1f1f1f',
    colorBgElevated: '#262626',
    colorBgLayout: '#0a0a0a',
    colorBgSpotlight: '#2a2a2a',

    // Text colors
    colorTextBase: '#ffffff',
    colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
    colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
    colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',

    // Border colors
    colorBorder: '#434343',
    colorBorderSecondary: '#303030',

    // Fill colors for hover states
    colorFillSecondary: 'rgba(255, 255, 255, 0.08)',
    colorFillTertiary: 'rgba(255, 255, 255, 0.04)',
    colorFillQuaternary: 'rgba(255, 255, 255, 0.02)',

    // Primary color adjustments for dark theme
    colorPrimaryBg: '#111d2c',
    colorPrimaryBgHover: '#112a45',
    colorPrimaryBorder: '#15395b',
    colorPrimaryBorderHover: '#1765ad',
    colorPrimaryHover: '#3c9ae8',
    colorPrimaryActive: '#0958d9',
    colorPrimaryTextHover: '#3c9ae8',
    colorPrimaryText: '#1890ff',
    colorPrimaryTextActive: '#0958d9',
  },
  components: {
    ...theme.components,

    // Layout
    Layout: {
      headerBg: '#1f1f1f',
      siderBg: '#1f1f1f',
      bodyBg: '#000000',
      headerHeight: 64,
    },

    // Card
    Card: {
      ...theme.components?.Card,
      colorBgContainer: '#262626',
      colorBorderSecondary: '#303030',
    },

    // Collapse
    Collapse: {
      headerBg: '#1f1f1f',
      contentBg: '#141414',
      colorBorder: '#303030',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
    },

    // Select
    Select: {
      colorBgContainer: '#1f1f1f',
      colorBgElevated: '#262626',
      colorBorder: '#434343',
      colorPrimaryHover: '#3c9ae8',
      optionSelectedBg: '#111d2c',
      optionSelectedColor: '#1890ff',
      optionActiveBg: 'rgba(255, 255, 255, 0.08)',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
    },

    // Input
    Input: {
      colorBgContainer: '#1f1f1f',
      colorBorder: '#434343',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.45)',
      colorText: 'rgba(255, 255, 255, 0.85)',
      activeBorderColor: '#1890ff',
      hoverBorderColor: '#3c9ae8',
      activeShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
    },

    // Button
    Button: {
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
      colorBgContainer: '#1f1f1f',
      colorBorder: '#434343',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorPrimaryHover: '#3c9ae8',
      colorPrimaryActive: '#0958d9',
      defaultShadow: 'none',
      primaryShadow: 'none',
      dangerShadow: 'none',
      defaultBorderColor: '#434343',
      defaultColor: 'rgba(255, 255, 255, 0.85)',
      defaultBg: '#1f1f1f',
      defaultHoverBorderColor: '#3c9ae8',
      defaultHoverColor: '#3c9ae8',
      defaultHoverBg: '#2a2a2a',
      defaultActiveBorderColor: '#1890ff',
      defaultActiveBg: '#1a1a1a',
      defaultActiveColor: '#1890ff',
    },

    // Switch
    Switch: {
      colorPrimary: '#1890ff',
      colorPrimaryHover: '#3c9ae8',
      colorTextQuaternary: '#434343',
      colorTextTertiary: '#595959',
    },

    // Radio
    Radio: {
      colorBorder: '#434343',
      colorBgContainer: '#1f1f1f',
      colorPrimary: '#1890ff',
      colorPrimaryHover: '#3c9ae8',
      colorText: 'rgba(255, 255, 255, 0.85)',
      buttonCheckedBg: '#1f1f1f',
      buttonCheckedBgDisabled: '#2a2a2a',
      buttonSolidCheckedColor: '#ffffff',
      buttonSolidCheckedBg: '#1890ff',
      buttonColor: 'rgba(255, 255, 255, 0.85)',
      buttonBg: '#1f1f1f',
      dotColorDisabled: '#434343',
    },

    // Message
    Message: {
      contentBg: '#262626',
      colorText: 'rgba(255, 255, 255, 0.85)',
    },

    // Modal
    Modal: {
      contentBg: '#1f1f1f',
      headerBg: '#1f1f1f',
      colorBgElevated: '#262626',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      borderRadiusLG: 8,
    },

    // Drawer
    Drawer: {
      colorBgElevated: '#1f1f1f',
      colorText: 'rgba(255, 255, 255, 0.85)',
    },

    // Table
    Table: {
      headerBg: '#262626',
      headerSplitColor: '#303030',
      colorBgContainer: '#1f1f1f',
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorTextHeading: 'rgba(255, 255, 255, 0.85)',
      rowHoverBg: '#2a2a2a',
    },

    // Form
    Form: {
      labelColor: 'rgba(255, 255, 255, 0.85)',
    },

    // Typography
    Typography: {
      colorText: 'rgba(255, 255, 255, 0.85)',
      colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
    },

    // Tabs
    Tabs: {
      colorBorderSecondary: '#303030',
      colorText: 'rgba(255, 255, 255, 0.65)',
      colorTextDescription: 'rgba(255, 255, 255, 0.45)',
      itemActiveColor: '#1890ff',
      itemHoverColor: '#3c9ae8',
      itemSelectedColor: '#1890ff',
    },

    // Dropdown
    Dropdown: {
      colorBgElevated: '#262626',
      colorText: 'rgba(255, 255, 255, 0.85)',
      controlItemBgHover: 'rgba(255, 255, 255, 0.08)',
      controlItemBgActive: '#111d2c',
    },

    // Spin
    Spin: {
      colorPrimary: '#1890ff',
    },
  },
}
